import { Store } from '@ngrx/store';

import { setHeaderHeight } from 'ish-core/store/core/viewconf';

/**
 * CUSTOMIZATION: MVP 8.2.1 / 8.2.2 — keep sticky clearance + mobile account-drawer
 * offset in sync with CSR / sales-rep context banners ("Now viewing as" / Viewing account).
 *
 * Both banners share `--prs-impersonation-banner-height`. Measuring the shared
 * `.prs-account-context-row` (and composing header chrome + row) avoids sticky TOC /
 * filter rails being cut off under the context strip on PDP and elsewhere.
 *
 * Presidio desktop nests the context row inside `.custom-header` — do not add its
 * height twice or PDP sticky TOC (`top: var(--prs-sticky-clearance)`) leaves a gap.
 */
export function syncPresidioContextBannerChrome(store?: Store): number {
  if (SSR || typeof document === 'undefined') {
    return 0;
  }

  const contextRow = document.querySelector<HTMLElement>('.prs-account-context-row');
  const banners = document.querySelectorAll<HTMLElement>(
    '.prs-impersonation-banner, .prs-sales-account-banner, .prs-impersonation-picker'
  );
  const hasContext =
    !!contextRow &&
    (contextRow.querySelector('.prs-account-strip') != null ||
      contextRow.querySelector('.prs-impersonation-picker') != null);
  const contextHeight = hasContext ? contextRow.offsetHeight || 0 : 0;
  const bannerHeight = hasContext
    ? contextHeight
    : Array.from(banners).reduce((max, el) => Math.max(max, el.offsetHeight || 0), 0);

  const root = document.documentElement;
  root.style.setProperty('--prs-impersonation-banner-height', `${bannerHeight}px`);
  root.classList.toggle('prs-cobrowsing', bannerHeight > 0);

  const header = document.getElementById('header');
  if (!header) {
    return bannerHeight;
  }

  const stickyWrapper = header.closest('.custom-sticky-header') as HTMLElement | null;
  const customHeader = header.querySelector<HTMLElement>('.custom-header, .prs-header');
  const headerChromeHeight = customHeader?.offsetHeight || 0;
  const contextNestedInChrome =
    !!customHeader && !!contextRow && hasContext && customHeader.contains(contextRow);
  // Desktop Presidio: context row is a child of `.custom-header` — subtract before composing.
  const chromeOnlyHeight = contextNestedInChrome
    ? Math.max(0, headerChromeHeight - contextHeight)
    : headerChromeHeight;
  const composed = chromeOnlyHeight + (hasContext ? contextHeight : 0);
  // Prefer live bottom of fixed sticky chrome; fall back to composed chrome + context row.
  const viewportBottom = Math.round((stickyWrapper || header).getBoundingClientRect().bottom);
  const measured =
    stickyWrapper && viewportBottom > 0
      ? viewportBottom
      : Math.max(header.offsetHeight || 0, composed);

  if (viewportBottom > 0) {
    // Mobile account drawer / backdrop start below real chrome (logo + context banner).
    root.style.setProperty('--prs-account-nav-drawer-top', `${viewportBottom}px`);
  }

  if (measured <= 0) {
    return bannerHeight;
  }

  // Keep page spacer + sticky `top` in sync whenever context chrome appears/resizes —
  // including while the header is already sticky (otherwise PDP TOC sits under the row).
  if (store) {
    store.dispatch(setHeaderHeight({ headerHeight: measured }));
  }
  root.style.setProperty('--prs-sticky-clearance', `${measured}px`);

  return bannerHeight;
}

/** Remeasure after a banner unmounts so a sibling context banner keeps clearance. */
export function clearPresidioContextBannerChrome(store?: Store): void {
  syncPresidioContextBannerChrome(store);
}

/** Remeasure after layout (banner *ngIf / display:contents hosts). */
export function schedulePresidioContextBannerChromeSync(store?: Store): void {
  if (SSR || typeof window === 'undefined') {
    return;
  }
  queueMicrotask(() => {
    syncPresidioContextBannerChrome(store);
    requestAnimationFrame(() => syncPresidioContextBannerChrome(store));
  });
}
